import { Stack, Popover, Typography, Button, Divider, useTheme, IconButton } from '@mui/material';
import { useForm, useWatch } from 'react-hook-form';
import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';

import { FormProvider, Iconify, RHFTextField, Scrollbar } from '@app/components';
import { useRequestState } from '@app/hooks';
import { ILabel } from '../types';
import { Strings } from '@app/constants';
import { addLabel } from '../slice';
import { LabelColors } from '../view/colors';
import { selectAddLabelState, selectDeleteLabelState } from '../selector';

interface Props {
  openCreateLabelPopup: boolean;
  handleCloseCreateLabelPopup: () => void;
  boardId: number;
}
const CreateLabelPopup = ({
  openCreateLabelPopup,
  handleCloseCreateLabelPopup,
  boardId,
}: Props) => {
  const open = Boolean(openCreateLabelPopup);
  const id = open ? 'simple-popover' : undefined;
  const theme = useTheme();
  const dispatch = useDispatch();
  const methods = useForm<ILabel>();

  const onSubmit = (form: ILabel) => {
    console.log('form', form);
    dispatch(addLabel({ ...form, boardId }));
    handleCloseCreateLabelPopup();
  };

  const handleOnSuccess = useCallback(() => {
    handleCloseCreateLabelPopup();
  }, []);

  useRequestState({
    stateSelector: selectDeleteLabelState,
    onSuccess: handleOnSuccess,
  });
  useRequestState({
    stateSelector: selectAddLabelState,
    onSuccess: handleOnSuccess,
  });

  const selectedColor = useWatch({ control: methods.control, name: 'color' });
  const labelTitle = useWatch({ control: methods.control, name: 'title' });
  const labelColors = LabelColors();

  useEffect(() => {
    if (open) {
      methods.reset();
    }
  }, [methods, open]);

  return (
    <>
      <FormProvider methods={methods}>
        <Stack sx={{ width: '30%' }} mt={4}>
          <Popover
            id={id}
            open={open}
            onClose={handleCloseCreateLabelPopup}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'center',
            }}
            sx={{
              '& .MuiPaper-root': {
                width: '340px',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              },
            }}
          >
            <Scrollbar>
              <Stack
                p={2}
                display={'flex'}
                flexDirection="row"
                justifyContent="space-between"
                alignItems={'center'}
              >
                <Typography variant="h6" sx={{ textAlign: 'center' }}>
                  {Strings.taskBoard.createLabels}
                </Typography>
                <IconButton
                  aria-label="close"
                  sx={{
                    color: theme.palette.common.black,
                  }}
                  onClick={handleCloseCreateLabelPopup}
                >
                  <Iconify icon={'mi:close'} />
                </IconButton>
              </Stack>
              <Stack
                sx={{
                  width: '100%',
                  height: '100px',
                  backgroundColor: theme.palette.grey[200],
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Stack
                  sx={{
                    width: '70%',
                    backgroundColor: selectedColor,
                    height: '35px',
                    alignItems: 'center',
                    color: 'black',
                  }}
                >
                  {labelTitle}
                </Stack>
              </Stack>
              <Stack p={2}>
                <Stack>
                  <Typography>{Strings.taskBoard.title}</Typography>
                  <RHFTextField name="title" size="small" />
                </Stack>
                <Stack mt={1}>
                  <Typography>{Strings.taskBoard.selectAColor}</Typography>

                  <Stack
                    mt={1}
                    spacing={1}
                    sx={{
                      display: 'flex',
                      flexDirection: 'row',
                      flexWrap: 'wrap',
                      justifyContent: 'space-between',
                    }}
                  >
                    {labelColors.map((color, index) => (
                      <Stack
                        key={index}
                        onClick={() => {
                          methods.setValue('color', color);
                          methods.setValue('id', index);
                        }}
                        sx={{
                          width: '18%',
                          height: '38px',
                          backgroundColor: color,
                          borderRadius: '4px',
                          border:
                            color === selectedColor
                              ? `2px solid ${theme.palette.common.black}`
                              : 'none',
                        }}
                      />
                    ))}
                  </Stack>
                </Stack>
                <Stack mt={2}>
                  <Divider />
                </Stack>
                <Stack
                  mt={1.5}
                  sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'flex-end' }}
                >
                  <Button
                    variant="contained"
                    sx={{ backgroundColor: theme.palette.quaternary.main, borderRadius: '5px' }}
                    onClick={methods.handleSubmit(onSubmit)}
                  >
                    {Strings.taskBoard.save}
                  </Button>
                </Stack>
              </Stack>
            </Scrollbar>
          </Popover>
        </Stack>
      </FormProvider>
    </>
  );
};
export { CreateLabelPopup };
